import { $ } from "#root/dashboard/l10n";
import { cancelAction, delayAction } from "#root/lib/delay_action";
import { icon } from "#root/lib/dom";
import { numberSpacing } from "#root/lib/formatting";
import { prepareCanvas } from "../lib/canvas";
import { dateStringToDate } from "../lib/conversions";
import { MonotoneCubicInterpolation } from "../lib/interpolation";
import { measureText } from "../lib/layouting";
let zoomHints = [];
let isZoomHintsDismissed = localStorage.getItem("isZoomHintsDismissed") === "true";
export async function lineChart(canvas, {
  sets,
  minY,
  maxY
}, {
  axisX,
  axisY,
  viewport,
  activeSetId,
  selfRepaint,
  selfRepaintArguments = {}
} = {}) {
  const {
    ctx
  } = prepareCanvas(canvas);
  let paintMaxY = maxY;

  if (selfRepaintArguments.maxY !== undefined) {
    paintMaxY = selfRepaintArguments.maxY;
  }

  let zoomHint;

  function dismissZoomHint() {
    localStorage.setItem("isZoomHintsDismissed", "true");
    isZoomHintsDismissed = true;
    zoomHints.forEach(hint => {
      hint.style.transition = "opacity 0.3s";
      hint.style.opacity = 0;
      setTimeout(() => {
        hint.remove();
      }, 300);
    });
  } // render y axis


  let yScale = [minY, minY + (paintMaxY - minY) / 4, minY + (paintMaxY - minY) / 2, minY + (paintMaxY - minY) / 4 * 3, paintMaxY]; // identify value with least amount of digits after the decimal point

  let decimalPlaces = -Infinity;

  for (let i = 0; i < yScale.length; i++) {
    const parts = String(yScale[i]).split(".");

    if (parts.length > 1) {
      decimalPlaces = Math.max(decimalPlaces, parts[1].length);
    } else {
      decimalPlaces = 0;
    }
  }

  if (decimalPlaces > 2) decimalPlaces = 2;

  for (let i = 0; i < yScale.length; i++) {
    const el = document.createElement("div");
    el.classList.add("chart-axis-label");
    el.textContent = numberSpacing(yScale[i], decimalPlaces);

    if (selfRepaintArguments) {
      el.classList.add("interactive");
      el.addEventListener("click", () => {
        dismissZoomHint();
        selfRepaint(false, true, {
          maxY: i === yScale.length - 1 ? maxY : yScale[i]
        });
      });

      if (i === 1 && !isZoomHintsDismissed) {
        el.addEventListener("mouseenter", () => {
          if (zoomHint && !isZoomHintsDismissed) zoomHint.active();
        });
        el.addEventListener("mouseleave", () => {
          if (zoomHint && !isZoomHintsDismissed) zoomHint.dismiss();
        });
      }
    }

    axisY.appendChild(el);
  }

  var dpi = window.devicePixelRatio || 1;
  var rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpi;
  canvas.height = rect.height * dpi;
  const {
    width,
    height
  } = canvas; // Decide on scale of the grid

  let minX = Infinity;
  let maxX = -Infinity; // detect minimal and maximal values for x across all sets

  for (let set of sets) {
    if (set.points.length === 0) continue;
    const comparisonX0 = dateStringToDate(set.points[0].x);
    const comparisonX1 = dateStringToDate(set.points[set.points.length - 1].x);
    if (comparisonX0.getTime() < minX) minX = comparisonX0.getTime();
    if (comparisonX1.getTime() > maxX) maxX = comparisonX1.getTime();
  } // decide what values to show on the x axis based on the range of the data


  let xGridPrecisionMode; // "day", "date", "month", "month/year", "year"

  const timeDifference = maxX - minX;

  if (timeDifference <= 1000 * 60 * 60 * 24 * 7) {
    xGridPrecisionMode = "day";
  } else if (timeDifference <= 1000 * 60 * 60 * 24 * 31) {
    xGridPrecisionMode = "date";
  } else if (timeDifference < 1000 * 60 * 60 * 24 * 365) {
    xGridPrecisionMode = "month";
  } else if (timeDifference < 1000 * 60 * 60 * 24 * 365 * 3) {
    xGridPrecisionMode = "month/year";
  } else {
    xGridPrecisionMode = "year";
  } // find key points in the set which means a change in the property targeted by xGridPrecisionMode


  let setsKeyPoints = Array(sets.length);
  let axisSignificantMarkers = [];
  let lastMarker;

  function addKeyPoint(setIndex, pointIndex, value, numericTag) {
    const date = dateStringToDate(value.x);
    let tag;

    if (xGridPrecisionMode === "day") {
      tag = date.toLocaleDateString('uk-UA', {
        weekday: "long"
      });
    } else if (xGridPrecisionMode === "date") {
      tag = date.toLocaleDateString('uk-UA', {
        month: "short",
        day: "numeric"
      });
    } else if (xGridPrecisionMode === "month") {
      tag = date.toLocaleDateString('uk-UA', {
        month: "long"
      });
    } else if (xGridPrecisionMode === "month/year") {
      tag = date.toLocaleDateString('uk-UA', {
        month: "long",
        year: "numeric"
      });
    } else {
      tag = date.toLocaleDateString('uk-UA', {
        year: "numeric"
      });
    }

    setsKeyPoints[setIndex].push({
      index: pointIndex,
      tag: tag,
      numericTag: numericTag,
      value
    });
    if (lastMarker && (lastMarker.end === undefined || lastMarker.end < pointIndex)) lastMarker.end = pointIndex - 1;
    const existingMarker = axisSignificantMarkers.find(marker => marker.numericTag === numericTag);

    if (!existingMarker) {
      lastMarker = {
        tag,
        numericTag,
        start: pointIndex
      };
      axisSignificantMarkers.push(lastMarker);
    } else {
      lastMarker = existingMarker;
      if (lastMarker.start > pointIndex) lastMarker.start = pointIndex;
    }
  } // Contains min and max values for each set


  let setsMaxMin = Array(sets.length);

  for (let setIndex = 0; setIndex < sets.length; setIndex++) {
    const set = sets[setIndex];
    setsKeyPoints[setIndex] = [];

    for (let pointIndex = 0; pointIndex < set.points.length; pointIndex++) {
      const point = set.points[pointIndex];
      const date = dateStringToDate(point.x); // update min/max values for the set

      if (setsMaxMin[setIndex] === undefined) {
        setsMaxMin[setIndex] = {
          min: point.y,
          max: point.y
        };
      } else {
        if (point.y < setsMaxMin[setIndex].min) setsMaxMin[setIndex].min = point.y;
        if (point.y > setsMaxMin[setIndex].max) setsMaxMin[setIndex].max = point.y;
      }

      let numericTag;

      if (xGridPrecisionMode === "day") {
        numericTag = date.getDay();
        if (numericTag === 0) numericTag = 7;
      } else if (xGridPrecisionMode === "date") {
        numericTag = date.getDate();
      } else if (xGridPrecisionMode === "month") {
        numericTag = date.getMonth() + date.getFullYear() * 12;
      } else if (xGridPrecisionMode === "month/year") {
        numericTag = date.getMonth() + date.getFullYear() * 12;
      } else {
        numericTag = date.getFullYear();
      }

      if (setsKeyPoints[setIndex].length === 0 || numericTag != setsKeyPoints[setIndex][setsKeyPoints[setIndex].length - 1].numericTag) {
        addKeyPoint(setIndex, pointIndex, point, numericTag);
      }
    }

    if (lastMarker && (lastMarker.end === undefined || lastMarker.end < set.points.length - 1)) lastMarker.end = set.points.length - 1;
  } // If one of the set has max value that is more than 96% larger that some other set's value, we show a hint


  if (!isZoomHintsDismissed && setsMaxMin.length > 1) {
    let maxDifference = 0;

    for (let i = 0; i < setsMaxMin.length; i++) {
      for (let j = i + 1; j < setsMaxMin.length; j++) {
        if (setsMaxMin[i].max === setsMaxMin[i].min || setsMaxMin[j].max === setsMaxMin[j].min) {
          continue;
        }

        const difference = Math.abs(setsMaxMin[i].max - setsMaxMin[j].max) / Math.max(setsMaxMin[i].max, setsMaxMin[j].max);
        if (difference > maxDifference) maxDifference = difference;
      }
    }

    if (maxDifference > 0.95) {
      zoomHint = document.createElement("div");
      zoomHint.classList.add("chart-zoom-hint");
      const hintIcon = icon(require("@Resources/images/icons/lightbulb.svg"));
      zoomHint.appendChild(hintIcon);
      const hintText = document.createElement("span");
      hintText.textContent = $("click_mark_to_zoom");
      zoomHint.appendChild(hintText);
      zoomHint.style.top = 100 / (axisY.children.length - 1) * 3 + "%";
      zoomHint.addEventListener("click", () => {
        if (!zoomHint.classList.contains("active")) return;
        dismissZoomHint();
      });

      zoomHint.active = () => {
        zoomHint.classList.add("active");
        hintText.style.position = "fixed";
        hintText.style.opacity = 0;
        hintText.style.width = "auto";
        requestAnimationFrame(() => {
          const hintTextRect = hintText.getBoundingClientRect();
          hintText.style.width = "0";
          hintText.style.position = "";
          hintText.style.opacity = "";
          requestAnimationFrame(() => {
            let width = hintTextRect.width;

            if (width + 200 > window.innerWidth) {
              width = window.innerWidth - 200;
              hintText.style.whiteSpace = "normal";
              hintText.style.transition = "unset";
            }

            hintText.style.width = width + "px";
          });
        });
      };

      zoomHint.dismiss = () => {
        zoomHint.classList.remove("active");
        requestAnimationFrame(() => {
          hintText.style.width = "";
          hintText.style.whiteSpace = "";
          hintText.style.transition = "";
        });
      };

      zoomHint.addEventListener("mouseenter", () => {
        setTimeout(() => {
          zoomHint.active();
        }, 0);
      });
      zoomHint.addEventListener("mouseleave", zoomHint.dismiss);
      canvas.parentElement.appendChild(zoomHint);
      zoomHints.push(zoomHint);
    }
  } // clean up and sort the axisSignificantMarkersSet


  axisSignificantMarkers = axisSignificantMarkers.sort((a, b) => a.numericTag - b.numericTag); // remove last element
  // axisSignificantMarkers.pop();
  // adapt the axisSignificantMarkersSet to fit the width of the canvas

  while (axisSignificantMarkers.reduce((acc, marker) => acc + measureText(marker.tag).width + 10, 0) > rect.width) {
    // remove every even element and extend previous element end value
    let removedCount = 0;

    for (let i = 0; i < axisSignificantMarkers.length; i++) {
      // starts from zero = so we need to remove odd items in the array
      if ((i + removedCount) % 2 !== 0) {
        if (i > 0) axisSignificantMarkers[i - 1].end = axisSignificantMarkers[i].end;
        axisSignificantMarkers.splice(i, 1);
        removedCount++;
        i--;
      }
    }

    if (removedCount === 0) {
      axisSignificantMarkers = [];
      break;
    }
  }

  const lineThicknessFactor = 4;
  const lineThickness = lineThicknessFactor * dpi;
  const availableHeight = height - 2 * lineThickness;
  const availableCssHeight = rect.height - 2 * lineThicknessFactor;
  let setsInterpolatedCurves = [];
  let setIndexes = Array.from({
    length: sets.length
  }, (_, i) => i); // move active set to the end

  if (activeSetId !== undefined) setIndexes.push(setIndexes.splice(setIndexes.indexOf(activeSetId), 1)[0]); // draw the lines

  for (let setIndex of setIndexes) {
    const set = sets[setIndex];
    const interpolatedCurve = new MonotoneCubicInterpolation(set.points, true, width);
    setsInterpolatedCurves.push(interpolatedCurve);
    let minCanvasY = height;
    const lowerBoundary = Math.floor(-lineThickness);
    const upperBoundary = Math.ceil(width + lineThickness);

    function drawCurve(height) {
      for (let i = lowerBoundary; i < upperBoundary; i++) {
        let y;

        if (i < 0 || i >= width) {
          if (i < 0) {
            y = interpolatedCurve.result[0].y - interpolatedCurve.result[0].y * (Math.abs(i) / width);
          } else {
            y = interpolatedCurve.result[interpolatedCurve.result.length - 1].y + interpolatedCurve.result[interpolatedCurve.result.length - 1].y * (Math.abs(i - width) / width);
          }
        } else {
          y = interpolatedCurve.result[i].y;
        }

        const yPos = (y - minY) / (paintMaxY - minY);
        const canvasY = height - yPos * height;
        if (canvasY < minCanvasY) minCanvasY = canvasY;
        ctx.lineTo(i, canvasY);
      }
    }

    if (set.fillStyle) {
      ctx.strokeStyle = "transparent";
      ctx.beginPath();
      drawCurve(availableHeight);
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);

      if (typeof set.fillStyle === "string") {
        ctx.fillStyle = set.fillStyle;
      } else {
        const stops = set.fillStyle.stops;
        const gradient = ctx.createLinearGradient(0, minCanvasY, 0, height);

        for (let stop of stops) gradient.addColorStop(stop.position, stop.color);

        ctx.fillStyle = gradient;
      }

      if (activeSetId !== undefined && activeSetId !== setIndex) {
        ctx.fillStyle = "#202020";
      }

      ctx.fill();
    }

    if (set.lineStyle) {
      ctx.strokeStyle = set.lineStyle;
      ctx.lineWidth = lineThickness;
      ctx.fillStyle = "transparent";

      if (activeSetId !== undefined && activeSetId !== setIndex) {
        ctx.strokeStyle = "#202020";
      }

      ctx.beginPath();
      drawCurve(availableHeight);
      ctx.stroke();
    } // draw keypoints on the curve

    /*for (let keyPoint of setsKeyPoints[setIndex]) {
        const point = keyPoint.value;
         const canvasX = keyPoint.index / (set.points.length - 1) * width;
        const canvasY = availableHeight - (point.y - minY) / (paintMaxY - minY) * availableHeight;
         ctx.fillStyle = set.lineStyle || "#000";
        ctx.beginPath();
        ctx.arc(canvasX, canvasY, 3 * dpi, 0, Math.PI * 2);
        ctx.fill();
    }*/

  }

  ctx.strokeStyle = "#1F1F1F";
  ctx.lineWidth = 1 * dpi;
  ctx.globalAlpha = 1;
  const resolution = Math.max(...sets.map(set => set.points.length)) - 1;
  /* for (let i = 0; i < resolution; i++) {
      ctx.beginPath();
      ctx.moveTo(i * width / resolution, 0);
      ctx.lineTo(i * width / resolution, height);
      ctx.stroke();
  } */
  // render frame around canvas

  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 1 * dpi;
  ctx.globalAlpha = 0.07;
  ctx.strokeRect(0, 0, width, height); // 25 50 75 horizontal lines

  ctx.beginPath();
  ctx.moveTo(0, height / 4);
  ctx.lineTo(width, height / 4);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, height / 2);
  ctx.lineTo(width, height / 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, height / 4 * 3);
  ctx.lineTo(width, height / 4 * 3);
  ctx.stroke();
  ctx.globalAlpha = 1; // check if we need to display precise date labels

  let usePreciseLabel = false; // render x axis

  for (let [idx, marker] of axisSignificantMarkers.entries()) {
    const el = document.createElement("div");
    el.classList.add("chart-axis-label");
    el.textContent = String(marker.tag); // NOTE: I expect all sets to have the same nodes
    // el.style.width = `${(marker.end - marker.start + 1) / resolution * 100}%`;

    el.style.position = "absolute";
    el.style.left = `${marker.start / resolution * 100}%`;
    el.style.textAlign = "left";
    const markerLength = marker.end - marker.start;

    if (idx + 1 === axisSignificantMarkers.length && markerLength === 0) {
      el.style.transform = "translateX(-100%)";
    } else if (idx !== 0) {
      el.style.transform = "translateX(-50%)";
    }

    if (markerLength > 1) usePreciseLabel = true;
    axisX.appendChild(el);
  }

  axisX.style.position = "relative"; // INTERACTIVITY

  function calculatePoint(point, index, length) {
    const overlayX = index / (length - 1) * rect.width;
    const overlayY = availableCssHeight - (point.y - minY) / (paintMaxY - minY) * availableCssHeight;
    return {
      index,
      point,
      overlayX,
      overlayY
    };
  }

  const overlayContainer = document.createElement("div");
  overlayContainer.classList.add("chart-overlay");
  viewport.appendChild(overlayContainer);
  const followCursorLine = document.createElement("div");
  followCursorLine.classList.add("chart-overlay-line");
  const tooltip = document.createElement("div");
  tooltip.classList.add("chart-overlay-tooltip");
  const hoverPoints = [];

  for (let setIndex = 0; setIndex < sets.length; setIndex++) {
    const set = sets[setIndex];
    const point = document.createElement("div");
    point.classList.add("chart-overlay-point");
    point.style.setProperty("--color", set.color ?? set.lineStyle ?? set.fillStyle);
    hoverPoints.push(point);
  }

  const runningAnimations = {};
  const hoverPointPositions = [];

  function animateHoverPointToX(set, xIndex, id, length) {
    if (runningAnimations[id]) {
      runningAnimations[id].cancel();
    }

    let isCancelled = false;

    function cancelAnimation() {
      delete runningAnimations[id];
      isCancelled = true;
    }

    runningAnimations[id] = {
      cancel: cancelAnimation
    };

    function moveToPoint(x) {
      const closestPointIndex = set.result.findIndex((point, index) => point.x >= x || index === set.result.length - 1);
      const closestPoint = set.result[closestPointIndex];
      const point = calculatePoint(closestPoint, closestPoint.x, length);
      hoverPoints[id].style.left = `${point.overlayX}px`;
      hoverPoints[id].style.top = `${point.overlayY < 0 ? 0 : point.overlayY}px`;
      hoverPointPositions[id] = point;
    }

    let currentIndex = hoverPointPositions[id];

    if (currentIndex === undefined || currentIndex.index === xIndex) {
      moveToPoint(xIndex);
      cancelAnimation();
      return;
    }

    const distance = currentIndex.index - xIndex;
    const duration = 200;
    const startTime = performance.now();

    function step() {
      if (isCancelled) return;
      const timePassed = performance.now() - startTime;
      const progress = Math.min(timePassed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 2);
      const x = currentIndex.index - distance * easedProgress;
      moveToPoint(x);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        moveToPoint(xIndex);
        cancelAnimation();
      }
    }

    requestAnimationFrame(step);
  }

  let isOverlayAdded = false;
  let lastCursorX;
  let removalScheduled;
  let tooltipMoveScheduled;

  if (activeSetId === undefined) {
    // seems to be an expensive operation, so we only do it once here
    const trendIconElement = icon(require("@Resources/images/icons/trend_arrow.svg"));
    let lastAnimationRequest;
    const rect = canvas.getBoundingClientRect();
    canvas.addEventListener("mousemove", event => {
      if (removalScheduled) {
        clearTimeout(removalScheduled);
        removalScheduled = undefined;
        overlayContainer.style.transition = "";
        overlayContainer.style.opacity = "";
      }

      const x = event.clientX - rect.left;
      lastCursorX = x;
      if (lastAnimationRequest) cancelAction(lastAnimationRequest);
      lastAnimationRequest = delayAction(() => {
        if (lastCursorX === undefined) return;

        if (!isOverlayAdded) {
          overlayContainer.appendChild(followCursorLine);
          overlayContainer.appendChild(tooltip);

          for (let point of hoverPoints) overlayContainer.appendChild(point);
        }

        followCursorLine.style.left = `${lastCursorX}px`;
        let localKeyPoints = [];

        for (let setIndex = 0; setIndex < sets.length; setIndex++) {
          const set = sets[setIndex];
          localKeyPoints[setIndex] = [];
          let closestPoint;
          let closestDistance = Infinity;

          for (let pointIndex = 0; pointIndex < set.points.length; pointIndex++) {
            const point = set.points[pointIndex];
            const calculatedPoint = calculatePoint(point, pointIndex, set.points.length);
            const distanceX = Math.abs(calculatedPoint.overlayX - lastCursorX);

            if (distanceX < closestDistance) {
              closestDistance = distanceX;
              closestPoint = calculatedPoint;
            }
          }

          localKeyPoints[setIndex] = closestPoint;
        }

        const tooltipContent = document.createElement("div");
        tooltipContent.classList.add("chart-overlay-tooltip-content");
        const label = document.createElement("div");
        label.classList.add("chart-overlay-tooltip-label");

        try {
          if (usePreciseLabel) {
            const date = dateStringToDate(localKeyPoints[0].point.x);
            label.textContent = date.toLocaleDateString('uk-UA', {
              month: "long",
              day: sets[0].maxPointPrecision === "month" ? undefined : "numeric",
              year: "numeric"
            });
            label.style.whiteSpace = "nowrap";
          } else {
            let keyPointTag;

            for (let keyPointIndex = 0; keyPointIndex < setsKeyPoints[0].length; keyPointIndex++) {
              if (setsKeyPoints[0][keyPointIndex].value.x === localKeyPoints[0].point.x) {
                keyPointTag = setsKeyPoints[0][keyPointIndex].tag;
              }
            }

            if (!keyPointTag) {
              keyPointTag = localKeyPoints[0].point.x;
            }

            label.textContent = String(keyPointTag);
          }
        } catch (error) {
          label.textContent = "-";
        }

        tooltipContent.appendChild(label);
        let highestPoint = null;
        let indexLabels = [];

        for (let setIndex = 0; setIndex < sets.length; setIndex++) {
          const set = sets[setIndex];
          const keyPoint = localKeyPoints[setIndex];
          const el = document.createElement("div");
          el.classList.add("chart-overlay-tooltip-item");
          indexLabels.push(el);

          if (sets.length > 1) {
            const colorIndicator = document.createElement("div");
            colorIndicator.classList.add("chart-legend-item-color");
            colorIndicator.style.background = set.color ?? set.lineStyle ?? set.fillStyle;
            el.appendChild(colorIndicator);
          }

          const elText = document.createElement("div");
          elText.classList.add("chart-overlay-tooltip-item-text");

          if ("yFormatted" in keyPoint.point) {
            elText.textContent = keyPoint.point.yFormatted;
          } else {
            elText.textContent = numberSpacing(keyPoint.point.y, decimalPlaces);
            if (elText.textContent === "0" && keyPoint.point.y !== 0) elText.textContent = numberSpacing(keyPoint.point.y, 2);
          }

          el.appendChild(elText);

          if (keyPoint.point.delta) {
            const trendContainer = document.createElement("span");
            trendContainer.classList.add("trend-container");

            if (keyPoint.point.trend === "up") {
              trendContainer.classList.add("up");
            } else if (keyPoint.point.trend === "down") {
              trendContainer.classList.add("down");
            }

            if (["up", "down"].includes(keyPoint.point.trend)) {
              const arrowIcon = trendIconElement.cloneNode(true);
              arrowIcon.classList.add("trend-icon");
              trendContainer.appendChild(arrowIcon);
            }

            const trendDelta = document.createElement("span");
            trendDelta.classList.add("trend-delta");
            trendDelta.textContent = keyPoint.point.delta;
            trendContainer.appendChild(trendDelta);
            el.appendChild(trendContainer);
          }
          /*hoverPoints[setIndex].style.left = `${keyPoint.overlayX}px`;
          hoverPoints[setIndex].style.top = `${keyPoint.overlayY < 0 ? 0 : keyPoint.overlayY}px`;*/


          animateHoverPointToX(setsInterpolatedCurves[setIndex], keyPoint.index, setIndex, set.points.length);

          if (keyPoint.overlayY < 0) {
            hoverPoints[setIndex].style.opacity = 0;
            el.classList.add("overflown");
          } else {
            hoverPoints[setIndex].style.opacity = "";
            el.classList.remove("overflown");
          }

          if (!highestPoint || keyPoint.overlayY < highestPoint.overlayY) highestPoint = keyPoint;
        }

        if (indexLabels.length > 5) {
          tooltipContent.classList.add("compact");
          indexLabels = indexLabels.sort((a, b) => {
            let labelA = a.querySelector(".chart-overlay-tooltip-item-text").textContent.replace(/[^\d.-]/g, "");
            let labelB = b.querySelector(".chart-overlay-tooltip-item-text").textContent.replace(/[^\d.-]/g, "");
            if (labelA === "-") return -1;
            if (labelB === "-") return 1;
            let numberA = parseFloat(labelA);
            let numberB = parseFloat(labelB);
            if (isNaN(numberA) || isNaN(numberB)) return 0;
            return numberB - numberA;
          });
        } else {
          tooltipContent.classList.remove("compact");
        }

        let tooltipHeight = 0;

        for (let indexLabel of indexLabels) {
          if (tooltipHeight > 8) {
            const moreLabel = document.createElement("div");
            moreLabel.classList.add("chart-overlay-tooltip-label");
            moreLabel.classList.add("chart-overlay-tooltip-hint");
            moreLabel.classList.add("more");
            moreLabel.textContent = $("zoom_to_see_more");
            tooltipContent.appendChild(moreLabel);
            break;
          }

          tooltipContent.appendChild(indexLabel);

          if (!indexLabel.classList.contains("overflown")) {
            tooltipHeight += 1;
          }
        } // show tooltip above the highest point


        tooltip.innerHTML = "";
        tooltip.appendChild(tooltipContent);
        if (tooltipMoveScheduled) cancelAnimationFrame(tooltipMoveScheduled);
        tooltipMoveScheduled = requestAnimationFrame(() => {
          tooltipMoveScheduled = undefined;
          lastAnimationRequest = undefined;
          const tooltipRect = tooltip.getBoundingClientRect();
          const factualLeft = Math.trunc(highestPoint.overlayX - tooltipRect.width / 2);
          const factualRight = Math.trunc(highestPoint.overlayX + tooltipRect.width);
          const bottomCalc = availableCssHeight - highestPoint.overlayY + 7 + 12;
          tooltip.style.bottom = `${bottomCalc > availableCssHeight ? availableCssHeight + 7 + 12 : bottomCalc}px`;

          if (factualLeft < 0) {
            tooltip.style.left = `${tooltipRect.width / 2 + 7}px`;
          } else if (factualRight > Math.trunc(rect.width)) {
            tooltip.style.left = `${rect.width - tooltipRect.width / 2 - 7}px`;
          } else {
            tooltip.style.left = `${highestPoint.overlayX}px`;
          }
        });
        lastCursorX = undefined;
        isOverlayAdded = true;
      });
    }, true);
    canvas.addEventListener("mouseleave", () => {
      overlayContainer.style.transition = "opacity 0.3s";
      overlayContainer.style.opacity = 0;
      removalScheduled = setTimeout(() => {
        overlayContainer.innerHTML = "";
        isOverlayAdded = false;
        overlayContainer.style.transition = "";
        overlayContainer.style.opacity = "";
      }, 300);
    }, true);
  }
}